import React, { useState, useEffect, useRef, useCallback } from 'react';
import { XMarkIcon, MagnifyingGlassIcon, MapPinIcon, ArrowsPointingOutIcon, StarIcon } from '@heroicons/react/24/outline';
import { apiGet } from '../../utils/api';
import MapboxMiniMap from '../maps/MapboxMiniMap';

interface LocationSearchResult {
    place_id: number;
    display_name: string;
    lat: string;
    lon: string;
    type: string;
}

interface RwandaBoundary {
    name: string;
    type: 'district' | 'sector';
    coordinates: [number, number][];
}

// Rwanda districts with approximate center coordinates
const RWANDA_DISTRICTS: { name: string; lat: number; lon: number }[] = [
    { name: 'Bugesera', lat: -2.1921, lon: 30.0676 },
    { name: 'Burera', lat: -1.6167, lon: 29.7500 },
    { name: 'Gakenke', lat: -1.6167, lon: 29.5667 },
    { name: 'Gasabo', lat: -1.9500, lon: 30.0833 },
    { name: 'Gatsibo', lat: -1.4833, lon: 30.4333 },
    { name: 'Gicumbi', lat: -1.5667, lon: 29.8833 },
    { name: 'Gisagara', lat: -2.2333, lon: 29.8333 },
    { name: 'Huye', lat: -2.6000, lon: 29.7000 },
    { name: 'Jalouz', lat: -1.6667, lon: 30.0833 },
    { name: 'Kamonyi', lat: -2.1000, lon: 29.8167 },
    { name: 'Karongi', lat: -2.1667, lon: 29.3667 },
    { name: 'Kayanza', lat: -2.9667, lon: 29.6333 },
    { name: 'Kicukiro', lat: -1.9167, lon: 30.0833 },
    { name: 'Kirehe', lat: -2.2500, lon: 30.8167 },
    { name: 'Muhanga', lat: -2.0667, lon: 29.6500 },
    { name: 'Musanze', lat: -1.5000, lon: 29.5333 },
    { name: 'Ngoma', lat: -2.1667, lon: 30.5667 },
    { name: 'Nyanza', lat: -2.3500, lon: 29.9167 },
    { name: 'Nyagatare', lat: -1.3333, lon: 30.3333 },
    { name: 'Nyamagabe', lat: -2.5667, lon: 29.5667 },
    { name: 'Nyamasheke', lat: -2.6167, lon: 29.3000 },
    { name: 'Nyamagabe', lat: -2.2833, lon: 29.4833 },
    { name: 'Ruhango', lat: -2.2333, lon: 29.9333 },
    { name: 'Rulindo', lat: -1.7167, lon: 29.6667 },
    { name: 'Rusizi', lat: -2.5167, lon: 28.9000 },
    { name: 'Rutana', lat: -3.9167, lon: 30.2500 },
    { name: 'Rwamagana', lat: -1.9500, lon: 30.4333 },
];

// Pre-defined sector boundaries (simplified - using center points as reference)
const RWANDA_SECTORS = [
    { name: 'Ruhengeri', district: 'Musanze' },
    { name: 'Rwanguba', district: 'Ruhango' },
    { name: 'Kinoni', district: 'Ruhango' },
    { name: 'Kabacuzi', district: 'Rwamagana' },
    { name: 'Muhazi', district: 'Rwamagana' },
    { name: 'Gashora', district: 'Bugesera' },
    { name: 'Juru', district: 'Rutsiro' },
    { name: 'Kivumu', district: 'Rutsiro' },
    { name: 'Gako', district: 'Kirehe' },
    { name: 'Gatunda', district: 'Kirehe' },
    { name: 'Remera', district: 'Gasabo' },
    { name: 'Kacyiru', district: 'Gasabo' },
    { name: 'Kimironko', district: 'Gasabo' },
    { name: 'Butare', district: 'Huye' },
    { name: 'Tumba', district: 'Huye' },
    { name: 'Maraba', district: 'Huye' },
    { name: 'Ngoma', district: 'Kicukiro' },
    { name: 'Kagarama', district: 'Kicukiro' },
    { name: 'Masaka', district: 'Kicukiro' },
    { name: 'Muko', district: 'Rusizi' },
    { name: 'Gikundamvura', district: 'Rusizi' },
];

interface FarmBlock {
    name: string;
    boundary_coordinates: [number, number][];
}

const normalizeBoundary = (value: any): [number, number][] => {
    if (!value) return [];
    let raw = value;
    if (typeof raw === 'string') {
        try {
            raw = JSON.parse(raw);
        } catch {
            return [];
        }
    }
    if (!Array.isArray(raw)) return [];
    return raw
        .map((point: any) => Array.isArray(point)
            ? [Number(point[0]), Number(point[1])]
            : [Number(point.lat ?? point.latitude), Number(point.lng ?? point.longitude)])
        .filter((point: number[]) => Number.isFinite(point[0]) && Number.isFinite(point[1])) as [number, number][];
};

const normalizeFarmBlocks = (value: any): FarmBlock[] => {
    if (!value) return [];
    let raw = value;
    if (typeof raw === 'string') {
        try {
            raw = JSON.parse(raw);
        } catch {
            return [];
        }
    }
    if (!Array.isArray(raw)) return [];
    return raw
        .map((block: any, index: number) => ({
            name: block?.name || `Farm block ${index + 1}`,
            boundary_coordinates: normalizeBoundary(block?.boundary_coordinates || block?.boundary || block?.coordinates),
        }))
        .filter((block: FarmBlock) => block.boundary_coordinates.length >= 3);
};

interface CreateCohortFormProps {
    onSubmit: (data: any) => Promise<void>;
    onCancel: () => void;
    initialData?: any;
}

const CreateCohortForm: React.FC<CreateCohortFormProps> = ({ onSubmit, onCancel, initialData }) => {
    const [loading, setLoading] = useState(false);
    const [crops, setCrops] = useState<any[]>([]);
    const [warehouses, setWarehouses] = useState<any[]>([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [searchResults, setSearchResults] = useState<LocationSearchResult[]>([]);
    const [isSearching, setIsSearching] = useState(false);
    const [selectedLocation, setSelectedLocation] = useState<LocationSearchResult | null>(null);
    const [mapType, setMapType] = useState<'street' | 'satellite' | 'hybrid'>('street');
    const [selectedDistrict, setSelectedDistrict] = useState('');
    const [mapCenter, setMapCenter] = useState<[number, number]>([-1.9441, 30.0619]);
    const [mapZoom, setMapZoom] = useState(13);
    const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    
    const [formData, setFormData] = useState({
        name: initialData?.name || '',
        cropping_system: initialData?.croppingSystem?.toLowerCase() || initialData?.cropping_system || 'avocado',
        location: initialData?.location || '',
        intercrops: Array.isArray(initialData?.intercrops) ? initialData.intercrops.join(', ') : '',
        target_area: initialData?.areaHa || initialData?.target_area || 1.0,
        start_date: initialData?.start_date || new Date().toISOString().split('T')[0],
        childcare_support: initialData?.childcare_support ?? true,
        repayment_threshold: initialData?.repayment_threshold || 85,
        seed_capital: initialData?.seed_capital || 300000,
        warehouse_assign: initialData?.warehouse_assign || '',
        boundary: normalizeBoundary(initialData?.boundary_coordinates) as [number, number][],
        farm_boundaries: normalizeFarmBlocks(initialData?.farm_boundaries) as FarmBlock[],
        farm_block_name: ''
    });

    useEffect(() => {
        fetchCrops();
        fetchWarehouses();
    }, []);

    // Search for locations using backend-proxied Mapbox geocoding
    const searchLocations = async (query: string) => {
        if (query.length < 3) {
            setSearchResults([]);
            return;
        }

        setIsSearching(true);
        try {
            const data = await apiGet(`/api/maps/search?q=${encodeURIComponent(query)}&limit=5`) as any;
            const results: LocationSearchResult[] = (data.results || data || [])
                .filter((r: any) => r.center)
                .map((r: any, idx: number) => ({
                    place_id: r.entityId || idx,
                    display_name: r.secondaryLabel || r.label,
                    lat: String(r.center[1]),
                    lon: String(r.center[0]),
                    type: r.entityType || r.source || 'place',
                }));
            setSearchResults(results);
        } catch (error) {
            console.error('Location search error:', error);
            setSearchResults([]);
        } finally {
            setIsSearching(false);
        }
    };

    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const query = e.target.value;
        setSearchQuery(query);
        setSelectedLocation(null);

        // Debounce search
        if (searchTimeoutRef.current) {
            clearTimeout(searchTimeoutRef.current);
        }
        searchTimeoutRef.current = setTimeout(() => {
            searchLocations(query);
        }, 300);
    };

    const handleSelectSearchResult = (result: LocationSearchResult) => {
        setSelectedLocation(result);
        setSearchQuery(result.display_name.split(',')[0]);
        setSearchResults([]);
        setMapCenter([parseFloat(result.lat), parseFloat(result.lon)]);
        setMapZoom(14);
        setFormData(prev => ({ ...prev, location: result.display_name.split(',')[0] }));
    };

    const handleDistrictChange = (districtName: string) => {
        setSelectedDistrict(districtName);
        const district = RWANDA_DISTRICTS.find(d => d.name === districtName);
        if (district) {
            setMapCenter([district.lat, district.lon]);
            setMapZoom(12);
            setFormData(prev => ({ ...prev, location: districtName }));
        }
    };

    const fetchCrops = async () => {
        try {
            const data = await apiGet('/api/crops') as any;
            const cropsArray = data.crops || data || [];
            const uniqueCrops = Array.from(
                new Map(cropsArray.map((crop: any) => [crop.crop_type || crop.product_type, crop])).values()
            );
            setCrops(uniqueCrops);
        } catch (error) {
            console.error('Failed to fetch crops:', error);
        }
    };

    const fetchWarehouses = async () => {
        try {
            const data = await apiGet('/api/warehouses/facilities') as any;
            setWarehouses(data || []);
        } catch (error) {
            console.error('Failed to fetch warehouses:', error);
        }
    };

    const handleAddBoundaryPoint = (lat: number, lng: number) => {
        setFormData(prev => ({
            ...prev,
            boundary: [...prev.boundary, [lat, lng]]
        }));
    };

    const handleRemoveLastPoint = () => {
        setFormData(prev => ({
            ...prev,
            boundary: prev.boundary.slice(0, -1)
        }));
    };

    const handleClearBoundary = () => {
        setFormData(prev => ({ ...prev, boundary: [] }));
    };

    const handleSaveFarmBlock = () => {
        if (formData.boundary.length < 3) {
            alert('Draw at least 3 points before saving a farm block');
            return;
        }
        const name = formData.farm_block_name.trim() || `Farm block ${formData.farm_boundaries.length + 1}`;
        setFormData(prev => ({
            ...prev,
            farm_boundaries: [
                ...prev.farm_boundaries,
                { name, boundary_coordinates: prev.boundary },
            ],
            farm_block_name: '',
            boundary: [],
        }));
    };

    const handleRemoveFarmBlock = (index: number) => {
        setFormData(prev => ({
            ...prev,
            farm_boundaries: prev.farm_boundaries.filter((_, itemIndex) => itemIndex !== index),
        }));
    };

    const handleRecenter = () => {
        if (selectedLocation) {
            setMapCenter([parseFloat(selectedLocation.lat), parseFloat(selectedLocation.lon)]);
            setMapZoom(14);
        } else if (selectedDistrict) {
            const district = RWANDA_DISTRICTS.find(d => d.name === selectedDistrict);
            if (district) {
                setMapCenter([district.lat, district.lon]);
                setMapZoom(12);
            }
        }
    };

    const calculateArea = () => {
        if (formData.boundary.length < 3) return 0;
        // Simple approximation using bounding box
        const lats = formData.boundary.map((p: [number, number]) => p[0]);
        const lngs = formData.boundary.map((p: [number, number]) => p[1]);
        const latDiff = Math.max(...lats) - Math.min(...lats);
        const lngDiff = Math.max(...lngs) - Math.min(...lngs);
        // Rough estimation: 1 degree ≈ 111km at equator
        return (latDiff * 111 * lngDiff * 111 * 100).toFixed(2);
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        
        if (!formData.name || !formData.location) {
            alert('Please fill in all required fields');
            return;
        }

        setLoading(true);
        try {
            const payload = {
                name: formData.name,
                cropping_system: formData.cropping_system,
                location: formData.location,
                intercrops: formData.intercrops.split(',').map((s: string) => s.trim()).filter(Boolean),
                target_area: formData.target_area,
                start_date: formData.start_date,
                childcare_support: formData.childcare_support,
                repayment_threshold: formData.repayment_threshold,
                seed_capital: formData.seed_capital,
                warehouse_assign: formData.warehouse_assign,
                boundary_coordinates: formData.boundary.length >= 3
                    ? formData.boundary
                    : formData.farm_boundaries[0]?.boundary_coordinates || [],
                farm_boundaries: formData.farm_boundaries.length > 0
                    ? formData.farm_boundaries
                    : (formData.boundary.length >= 3 ? [{
                        name: formData.farm_block_name.trim() || 'Farm block 1',
                        boundary_coordinates: formData.boundary,
                    }] : [])
            };

            await onSubmit(payload);
        } catch (error) {
            console.error('Form submission error:', error);
        } finally {
            setLoading(false);
        }
    };

    return (
        <form onSubmit={handleSubmit} className="space-y-6">
            {/* Basic Information */}
            <div className="bg-slate-50 rounded-xl p-6 border border-slate-200">
                <h3 className="text-lg font-semibold text-slate-900 mb-4">Basic Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">
                            Cohort Name <span className="text-red-500">*</span>
                        </label>
                        <input
                            type="text"
                            value={formData.name}
                            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                            className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            placeholder="e.g., Huye Avocado Group A"
                            required
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">
                            Location <span className="text-red-500">*</span>
                        </label>
                        <div className="relative">
                            <div className="relative">
                                <MagnifyingGlassIcon className="absolute left-3 top-3 w-5 h-5 text-slate-400" />
                                <input
                                    type="text"
                                    value={searchQuery}
                                    onChange={handleSearchChange}
                                    className="w-full pl-10 pr-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                    placeholder="Search location in Rwanda..."
                                />
                                {isSearching && (
                                    <div className="absolute right-3 top-3">
                                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
                                    </div>
                                )}
                            </div>
                            {/* Search Results Dropdown */}
                            {searchResults.length > 0 && (
                                <div className="absolute z-50 w-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                                    {searchResults.map((result) => (
                                        <button
                                            key={result.place_id}
                                            type="button"
                                            onClick={() => handleSelectSearchResult(result)}
                                            className="w-full text-left px-4 py-3 hover:bg-blue-50 border-b border-slate-100 last:border-b-0 transition-colors"
                                        >
                                            <div className="flex items-start">
                                                <MapPinIcon className="w-4 h-4 text-blue-500 mt-1 mr-2 flex-shrink-0" />
                                                <div>
                                                    <p className="text-sm font-medium text-slate-900 line-clamp-2">{result.display_name}</p>
                                                    <p className="text-xs text-slate-500 capitalize">{result.type}</p>
                                                </div>
                                            </div>
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">District Quick Select</label>
                        <select
                            value={selectedDistrict}
                            onChange={(e) => handleDistrictChange(e.target.value)}
                            className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        >
                            <option value="">Select a district...</option>
                            {RWANDA_DISTRICTS.map((district) => (
                                <option key={district.name} value={district.name}>
                                    {district.name}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">Cropping System</label>
                        <select
                            value={formData.cropping_system}
                            onChange={(e) => setFormData({ ...formData, cropping_system: e.target.value })}
                            className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        >
                            <option value="">Select crop...</option>
                            {crops.map((crop: any, index: number) => (
                                <option key={index} value={(crop.crop_type || crop.product_type || '').toLowerCase()}>
                                    {crop.crop_type || crop.product_type}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">Start Date</label>
                        <input
                            type="date"
                            value={formData.start_date}
                            onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                            className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                    </div>
                </div>
            </div>

            {/* Agricultural Details */}
            <div className="bg-slate-50 rounded-xl p-6 border border-slate-200">
                <h3 className="text-lg font-semibold text-slate-900 mb-4">Agricultural Details</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">Target Area (hectares)</label>
                        <input
                            type="number"
                            step="0.1"
                            min="0"
                            value={formData.target_area}
                            onChange={(e) => setFormData({ ...formData, target_area: parseFloat(e.target.value) })}
                            className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                        <p className="text-xs text-slate-500 mt-1">≈ {(formData.target_area * 2.47105).toFixed(2)} acres</p>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">Intercrops</label>
                        <input
                            type="text"
                            value={formData.intercrops}
                            onChange={(e) => setFormData({ ...formData, intercrops: e.target.value })}
                            className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            placeholder="e.g., Beans, Maize, Cassava"
                        />
                        <p className="text-xs text-slate-500 mt-1">Separate multiple crops with commas</p>
                    </div>
                </div>
            </div>

            {/* Financial & Support */}
            <div className="bg-slate-50 rounded-xl p-6 border border-slate-200">
                <h3 className="text-lg font-semibold text-slate-900 mb-4">Financial & Support</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">Seed Capital (RWF)</label>
                        <input
                            type="number"
                            step="1000"
                            min="0"
                            value={formData.seed_capital}
                            onChange={(e) => setFormData({ ...formData, seed_capital: parseInt(e.target.value) })}
                            className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">Repayment Threshold (%)</label>
                        <input
                            type="number"
                            min="0"
                            max="100"
                            value={formData.repayment_threshold}
                            onChange={(e) => setFormData({ ...formData, repayment_threshold: parseInt(e.target.value) })}
                            className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">Warehouse Assignment</label>
                        <select
                            value={formData.warehouse_assign}
                            onChange={(e) => setFormData({ ...formData, warehouse_assign: e.target.value })}
                            className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        >
                            <option value="">Select warehouse...</option>
                            {warehouses.map((warehouse: any) => (
                                <option key={warehouse.id} value={warehouse.id}>
                                    {warehouse.name} - {warehouse.location_name || 'Location not set'}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div className="flex items-center pt-8">
                        <input
                            type="checkbox"
                            checked={formData.childcare_support}
                            onChange={(e) => setFormData({ ...formData, childcare_support: e.target.checked })}
                            className="mr-3 h-5 w-5 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                        <label className="text-sm font-medium text-slate-700">Childcare Support Available</label>
                    </div>
                </div>
            </div>

            {/* Enhanced Boundary Drawing */}
            <div className="bg-slate-50 rounded-xl p-6 border border-slate-200">
                <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 mb-4">
                    <div>
                        <h3 className="text-lg font-semibold text-slate-900">Cohort Boundary</h3>
                        <p className="text-sm text-slate-600">Draw the boundary by clicking on the map</p>
                    </div>
                    <div className="flex flex-wrap items-center gap-3">
                        {/* Map Type Toggle */}
                        <div className="flex bg-white rounded-lg border border-slate-300 p-1">
                            <button
                                type="button"
                                onClick={() => setMapType('street')}
                                className={`px-3 py-1.5 text-sm rounded-md transition-colors ${
                                    mapType === 'street' ? 'bg-blue-600 text-white' : 'text-slate-600 hover:bg-slate-100'
                                }`}
                            >
                                Street
                            </button>
                            <button
                                type="button"
                                onClick={() => setMapType('satellite')}
                                className={`px-3 py-1.5 text-sm rounded-md transition-colors ${
                                    mapType === 'satellite' ? 'bg-blue-600 text-white' : 'text-slate-600 hover:bg-slate-100'
                                }`}
                            >
                                Satellite
                            </button>
                            <button
                                type="button"
                                onClick={() => setMapType('hybrid')}
                                className={`px-3 py-1.5 text-sm rounded-md transition-colors ${
                                    mapType === 'hybrid' ? 'bg-blue-600 text-white' : 'text-slate-600 hover:bg-slate-100'
                                }`}
                            >
                                Hybrid
                            </button>
                        </div>
                        {/* Recenter Button */}
                        {(selectedLocation || selectedDistrict) && (
                            <button
                                type="button"
                                onClick={handleRecenter}
                                className="flex items-center px-3 py-1.5 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                            >
                                <ArrowsPointingOutIcon className="w-4 h-4 mr-1" />
                                Recenter
                            </button>
                        )}
                        {/* Undo/Clear Buttons */}
                        <div className="flex gap-2">
                            <button
                                type="button"
                                onClick={handleRemoveLastPoint}
                                disabled={formData.boundary.length === 0}
                                className="px-3 py-1.5 text-sm bg-orange-500 text-white rounded-lg hover:bg-orange-600 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
                            >
                                Undo
                            </button>
                            <button
                                type="button"
                                onClick={handleClearBoundary}
                                disabled={formData.boundary.length === 0}
                                className="px-3 py-1.5 text-sm bg-red-500 text-white rounded-lg hover:bg-red-600 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
                            >
                                Clear
                            </button>
                        </div>
                    </div>
                </div>

                {/* Boundary Stats */}
                <div className="flex flex-wrap gap-4 mb-3">
                    <div className="flex items-center px-3 py-2 bg-blue-50 rounded-lg border border-blue-200">
                        <MapPinIcon className="w-5 h-5 text-blue-600 mr-2" />
                        <span className="text-sm">
                            <span className="font-semibold text-blue-900">{formData.boundary.length}</span> points
                        </span>
                    </div>
                    {formData.boundary.length >= 3 && (
                        <div className="flex items-center px-3 py-2 bg-green-50 rounded-lg border border-green-200">
                            <StarIcon className="w-5 h-5 text-green-600 mr-2" />
                            <span className="text-sm">
                                <span className="font-semibold text-green-900">~{calculateArea()}</span> hectares (estimated)
                            </span>
                        </div>
                    )}
                </div>

                <div className="mb-4 rounded-xl border border-emerald-100 bg-white p-4">
                    <div className="grid gap-3 md:grid-cols-[1fr_auto_auto] md:items-end">
                        <div>
                            <label className="block text-sm font-medium text-slate-700 mb-2">Farm block name</label>
                            <input
                                type="text"
                                value={formData.farm_block_name}
                                onChange={(e) => setFormData({ ...formData, farm_block_name: e.target.value })}
                                className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                placeholder={`Farm block ${formData.farm_boundaries.length + 1}`}
                            />
                        </div>
                        <button
                            type="button"
                            onClick={handleSaveFarmBlock}
                            disabled={formData.boundary.length < 3}
                            className="px-4 py-2.5 rounded-lg bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700 disabled:bg-slate-300 disabled:cursor-not-allowed"
                        >
                            Save farm block
                        </button>
                        <button
                            type="button"
                            onClick={handleClearBoundary}
                            disabled={formData.boundary.length === 0}
                            className="px-4 py-2.5 rounded-lg border border-slate-200 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                        >
                            Clear drawing
                        </button>
                    </div>
                    {formData.farm_boundaries.length > 0 && (
                        <div className="mt-4 grid gap-2 sm:grid-cols-2">
                            {formData.farm_boundaries.map((block, index) => (
                                <div key={`${block.name}-${index}`} className="flex items-center justify-between rounded-lg bg-emerald-50 px-3 py-2 text-sm">
                                    <div>
                                        <p className="font-bold text-emerald-900">{block.name}</p>
                                        <p className="text-xs text-emerald-700">{block.boundary_coordinates.length} boundary points</p>
                                    </div>
                                    <button
                                        type="button"
                                        onClick={() => handleRemoveFarmBlock(index)}
                                        className="rounded-md px-2 py-1 text-xs font-bold text-red-600 hover:bg-red-50"
                                    >
                                        Remove
                                    </button>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {/* Map Container */}
                <div className="h-[500px] rounded-lg overflow-hidden border-2 border-slate-300 relative">
                    <MapboxMiniMap
                        center={mapCenter}
                        zoom={mapZoom}
                        scrollWheelZoom={true}
                        basemap={mapType === 'satellite' || mapType === 'hybrid' ? 'satellite' : 'streets'}
                        onMapClick={handleAddBoundaryPoint}
                        cursor="crosshair"
                        polygons={[
                            ...formData.farm_boundaries.map((block, idx) => ({
                                id: `farm-block-${idx}`,
                                coordinates: block.boundary_coordinates,
                                color: '#059669',
                                fillColor: '#10B981',
                                fillOpacity: 0.2,
                                label: block.name,
                            })),
                            ...(formData.boundary.length > 2 ? [{
                                id: 'cohort-boundary',
                                coordinates: formData.boundary,
                                color: '#3B82F6',
                                fillColor: '#3B82F6',
                                fillOpacity: 0.2,
                            }] : []),
                        ]}
                        markers={formData.boundary.map((point, idx) => ({
                            id: `boundary-${idx}`,
                            position: point,
                            color: '#3B82F6',
                            label: String(idx + 1),
                        }))}
                    />
                    
                    {/* Map Help Overlay */}
                    {formData.boundary.length === 0 && (
                        <div className="absolute bottom-4 left-4 z-[1000] bg-white/95 backdrop-blur px-4 py-2 rounded-lg shadow-lg border border-slate-200">
                            <p className="text-sm text-slate-700">
                                <span className="font-medium">Click on the map</span> to draw boundary points
                            </p>
                        </div>
                    )}
                </div>
            </div>

            {/* Action Buttons */}
            <div className="flex justify-end gap-3 pt-4 border-t border-slate-200">
                <button
                    type="button"
                    onClick={onCancel}
                    className="px-6 py-2.5 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors font-medium"
                >
                    Cancel
                </button>
                <button
                    type="submit"
                    disabled={loading}
                    className="px-6 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors font-medium"
                >
                    {loading ? (initialData ? 'Updating...' : 'Creating...') : (initialData ? 'Update Cohort' : 'Create Cohort')}
                </button>
            </div>
        </form>
    );
};

export default CreateCohortForm;
